import { isPeopleLikeCameraType } from '../constants/adminPageConstants';

/**
 * Кратчайшая иерархия (ТЗ №24): объект → подразделение → область → локация → камера.
 *
 * Дерево — массив объектов в форме GET /factories/{id} (FactoryDetail: workshops → sections →
 * rooms → cameras), backend уже отдаёт только доступную пользователю иерархию.
 * Пустые ветви (без камер типа страницы) скрываются, уровни с единственным элементом
 * пропускаются: одна локация — сразу камеры, несколько — локации, несколько объектов —
 * объекты, а внутри объекта только значимые уровни.
 *
 * Позиция навигации — та же пара { level, items }, что хранит useNavigationState.
 */

const LEVELS = [
  { level: 'factories', item: 'factory', children: 'workshops' },
  { level: 'workshops', item: 'workshop', children: 'sections' },
  { level: 'sections', item: 'section', children: 'rooms' },
  { level: 'rooms', item: 'room', children: 'cameras' },
  { level: 'cameras', item: 'camera', children: null },
  { level: 'frames', item: null, children: null },
];

export const LOCATION_LEVELS = ['factories', 'workshops', 'sections', 'rooms'];

const ITEM_KEYS = ['factory', 'workshop', 'section', 'room', 'camera'];
const CHILD_KEYS = ['workshops', 'sections', 'rooms', 'cameras'];

const levelIndex = (level) => LEVELS.findIndex((entry) => entry.level === level);

export const emptyItems = () => ({ factory: null, workshop: null, section: null, room: null, camera: null });

const asArray = (value) => (Array.isArray(value) ? value : []);

/** Узел дерева без вложенных списков — для selectedItems и sessionStorage. */
export const toPathItem = (node) => {
  if (!node || typeof node !== 'object') return null;
  const item = { ...node };
  CHILD_KEYS.forEach((key) => { delete item[key]; });
  return item;
};

/** Камера показывается на странице этого типа: PEOPLE и PPE вместе, EQUIPMENT отдельно. */
export const isCameraVisibleForPage = (camera, pageCameraType, admin = false) => {
  if (!camera) return false;
  const typeMatches = isPeopleLikeCameraType(pageCameraType)
    ? isPeopleLikeCameraType(camera.type)
    : camera.type === pageCameraType;
  if (!typeMatches) return false;
  const hiddenForClient = camera.is_deleted || ['PENDING_DELETE', 'DELETED'].includes(camera.status);
  return admin || !hiddenForClient;
};

/** Уровень, который открывается после выбора элемента. */
export const LEVEL_AFTER = {
  factory: 'workshops',
  workshop: 'sections',
  section: 'rooms',
  room: 'cameras',
  camera: 'frames',
};

/** Полная иерархия без дерева (fallback): «Назад» на уровень выше. */
const FULL_DEPTH_BACK = {
  frames: 'cameras', cameras: 'rooms', rooms: 'sections', sections: 'workshops', workshops: 'factories',
};

/** Убирает ветви без подходящих камер. */
export const pruneHierarchy = (factories, isCameraVisible = () => true) => {
  const pruneLevel = (nodes, depth) => asArray(nodes).reduce((acc, node) => {
    if (!node || typeof node !== 'object') return acc;
    const childKey = CHILD_KEYS[depth];
    if (childKey === 'cameras') {
      const cameras = asArray(node.cameras).filter((camera) => isCameraVisible(camera));
      if (cameras.length) acc.push({ ...node, cameras });
      return acc;
    }
    const children = pruneLevel(node[childKey], depth + 1);
    if (children.length) acc.push({ ...node, [childKey]: children });
    return acc;
  }, []);
  return pruneLevel(factories, 0);
};

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/** Узел дерева по пути items для уровня itemKey (factory/workshop/section/room) либо null. */
const findNode = (tree, items, uptoIndex) => {
  let nodes = asArray(tree);
  let node = null;
  for (let i = 0; i <= uptoIndex; i += 1) {
    const wanted = items?.[ITEM_KEYS[i]];
    if (!wanted || typeof wanted !== 'object') return null;
    node = nodes.find((candidate) => sameId(candidate?.id, wanted.id)) || null;
    if (!node) return null;
    nodes = asArray(node[CHILD_KEYS[i]]);
  }
  return node;
};

/** Элементы уровня-списка (factories…rooms) при данном пути; null, если путь не найден. */
export const getLevelItems = (tree, level, items) => {
  const index = levelIndex(level);
  if (index < 0 || index > 3) return null;
  if (index === 0) return asArray(tree);
  const parent = findNode(tree, items, index - 1);
  return parent ? asArray(parent[CHILD_KEYS[index - 1]]) : null;
};

/** Уровень показывается, если на нём не ровно один элемент; камеры и кадры — всегда. */
export const isLevelShown = (tree, level, items) => {
  if (!LOCATION_LEVELS.includes(level)) return true;
  const list = getLevelItems(tree, level, items);
  return !list || list.length !== 1;
};

const clearFrom = (items, index) => {
  const next = { ...emptyItems(), ...(items || {}) };
  for (let i = index; i < ITEM_KEYS.length; i += 1) next[ITEM_KEYS[i]] = null;
  return next;
};

/** Спускается через уровни с единственным элементом до первого показанного уровня. */
export const descendPosition = (tree, level, items) => {
  let currentLevel = level;
  let currentItems = { ...emptyItems(), ...(items || {}) };
  while (LOCATION_LEVELS.includes(currentLevel)) {
    const list = getLevelItems(tree, currentLevel, currentItems);
    if (!list || list.length !== 1) break;
    const index = levelIndex(currentLevel);
    currentItems = clearFrom(currentItems, index);
    currentItems[ITEM_KEYS[index]] = toPathItem(list[0]);
    currentLevel = LEVELS[index + 1].level;
  }
  return { level: currentLevel, items: currentItems };
};

/**
 * Строит кратчайшую иерархию: убирает пустые ветви и находит стартовую позицию.
 * @returns {{ tree: Array, level: string, items: object }}
 */
export const buildShortestHierarchy = (tree, { isCameraVisible } = {}) => {
  const pruned = pruneHierarchy(tree, isCameraVisible);
  return { tree: pruned, ...descendPosition(pruned, 'factories', emptyItems()) };
};

/**
 * Приводит восстановленную позицию (в т. ч. из старой полной навигации) к кратчайшей:
 * путь в скрытую/несуществующую ветвь — на старт, пропускаемый уровень — вниз до показанного.
 */
export const normalizePosition = (tree, level, items) => {
  const index = levelIndex(level);
  if (index < 0) return descendPosition(tree, 'factories', emptyItems());
  const required = Math.min(index, 4); // число обязательных узлов-локаций в пути
  const fresh = clearFrom(items, required);
  for (let i = 0; i < required; i += 1) {
    const node = findNode(tree, items, i);
    if (!node) return descendPosition(tree, 'factories', emptyItems());
    fresh[ITEM_KEYS[i]] = toPathItem(node);
  }
  if (index === 5) fresh.camera = items?.camera ?? null;
  return descendPosition(tree, level, fresh);
};

/** Куда ведёт «Назад»: ближайший показанный уровень выше; null — выше некуда. */
export const getBackTarget = (tree, level, items) => {
  let index = levelIndex(level);
  if (index <= 0) return null;
  for (index -= 1; index >= 0; index -= 1) {
    const targetLevel = LEVELS[index].level;
    const targetItems = clearFrom(items, index);
    if (isLevelShown(tree, targetLevel, targetItems)) return { level: targetLevel, items: targetItems };
  }
  return null;
};

/** Позиция «открыт уровень level»: выбор на этом уровне и ниже сброшен. */
export const positionAtLevel = (level, items) => {
  const index = levelIndex(level);
  return { level, items: clearFrom(items, index < 0 ? 0 : index) };
};

/**
 * Позиция после выбора элемента item уровня itemLevel (factory…camera).
 * С деревом — сразу спуск через пропускаемые уровни; без дерева — следующий уровень.
 */
export const openItemPosition = (tree, itemLevel, item, items) => {
  const index = ITEM_KEYS.indexOf(itemLevel);
  if (index < 0) return null;
  const next = clearFrom(items, index);
  next[itemLevel] = toPathItem(item);
  const level = LEVEL_AFTER[itemLevel];
  return tree ? descendPosition(tree, level, next) : { level, items: next };
};

/**
 * «Назад»: с деревом — ближайший показанный уровень, без дерева — уровень выше.
 * null — кнопка неактивна.
 */
export const resolveBackTarget = (tree, level, items) => {
  if (tree) return getBackTarget(tree, level, items);
  const target = FULL_DEPTH_BACK[level];
  return target ? positionAtLevel(target, items) : null;
};

/**
 * Хлебные крошки: [{ key, label, target }]. target — уровень, который открывает крошка;
 * null — подпись без перехода (последняя камера или пропущенный уровень).
 * Корневая «Объекты» не показывается, если уровень объектов пропущен.
 */
export const buildCrumbs = (tree, items, rootLabel) => {
  const shown = (level) => !tree || isLevelShown(tree, level, items);
  const crumbs = [];
  if (shown('factories')) crumbs.push({ key: 'root', label: rootLabel, target: 'factories' });
  ITEM_KEYS.forEach((key) => {
    const item = items?.[key];
    if (!item) return;
    const target = key === 'camera' ? null : LEVEL_AFTER[key];
    crumbs.push({ key, label: item.name, target: target && shown(target) ? target : null });
  });
  return crumbs;
};

/** Позиции совпадают по уровню и id выбранных элементов. */
export const isSamePosition = (a, b) => a?.level === b?.level
  && ITEM_KEYS.every((key) => (a?.items?.[key]?.id ?? null) === (b?.items?.[key]?.id ?? null));
